<form
  [formGroup]="tableForm"
  autocomplete="off"
  (ngSubmit)="submit()"
  [class.disabled]="disabled"
>
  <div class="scroll-container">
    <table mat-table [dataSource]="dataSource" multiTemplateDataRows>
      @for (column of table.columns; track column) {
        <ng-container
          [matColumnDef]="column.column"
          [sticky]="sticky && column.isLabel"
          [stickyEnd]="sticky && column.isTotal"
        >
          <th scope="col" mat-header-cell *matHeaderCellDef>
            @if (!column.range || checkColumnHeaderRange()) {
              {{ column.headerName }}
            }
          </th>
          <td
            mat-cell
            *matCellDef="let element"
            [formArrayName]="element.isOtherSpecify ? 'other' : 'table'"
            [class.active]="
              isHighlightedRow(element.rowId) &&
              isHighlightedColumn(column.column)
            "
          >
            @let index = getRowIndex(element);
            @if (
              column.isId ||
              (column.isLabel &&
                (!element.isOtherSpecify ||
                  (element.isOtherSpecify && readOnly)))
            ) {
              {{ element[column.column] }}
            }
            @if (column.isId && element.idLabel) {
              {{ element.idLabel }}
            }
            @if (column.isLabel && element.isOtherSpecify && !readOnly) {
              <ng-container [formGroupName]="getOtherIndex(index)">
                <div class="flex-row other-input">
                  <mat-form-field>
                    <mat-label>Other (specify)</mat-label>
                    <input matInput formControlName="text" title="Other" />
                  </mat-form-field>
                  @if (
                    element.rowId === latestOtherRowId ||
                    element.idLabel === latestOtherRowId
                  ) {
                    <span class="flex-row other-actions">
                      @if (otherRows.length > 1) {
                        <button
                          mat-icon-button
                          type="button"
                          class="remove-other"
                          [disabled]="disabled"
                          (click)="
                            removeOtherRow(element.idLabel ?? element.rowId)
                          "
                          matTooltip="Remove this row"
                        >
                          <mat-icon>remove_circle_outline</mat-icon>
                        </button>
                      }
                      <button
                        [class.invisible]="otherRows.length === table.other.max"
                        mat-icon-button
                        type="button"
                        class="add-other"
                        [disabled]="disabled"
                        (click)="addOtherRow()"
                        matTooltip="Add another row"
                      >
                        <mat-icon>add_circle_outline</mat-icon>
                      </button>
                    </span>
                  }
                </div>
              </ng-container>
            }
            @if (
              !element.rowId &&
              !column.isId &&
              !column.isLabel &&
              !element.isOtherSpecify &&
              !element.total &&
              table.rows.current[index + 1] &&
              table.rows.current[index + 1].hasOwnProperty(column.column)
            ) {
              @if (
                !column.alternates || !checkColumnForAlternates(column, index)
              ) {
                <span>
                  {{ column.headerName }}
                </span>
              }
              @if (checkColumnForAlternates(column, index)) {
                <span>
                  {{ getAlternateColumnHeaderName(column, index) }}
                </span>
              }
            }
            @if (
              (element.rowId || (element.idLabel && element.isOtherSpecify)) &&
              columnTotals.hasOwnProperty(column.column) &&
              element.hasOwnProperty(column.column)
            ) {
              <ng-container
                [formGroupName]="
                  element.isOtherSpecify ? getOtherIndex(index) : index
                "
              >
                <app-number-input
                  class="input"
                  [class.active]="!readOnly"
                  [decimals]="getColumnDecimals(column.column)"
                  [formControlName]="column.column"
                  [displayOnly]="readOnly"
                  [isCurrency]="currency"
                  [max]="maxInputSize"
                  [hasSubIssue]="hasSubIssues(element.rowId, column.column)"
                >
                </app-number-input>
                @if (hasSubIssues(element.rowId, column.column)) {
                  <app-view-issue-link
                    id="{{ column.column }}-{{ element.rowId }}"
                    (popoverClose)="onPopoverClosed($event)"
                    (popoverOpen)="onPopoverOpen($event)"
                    (next)="onNextSubIssue()"
                    (previous)="onPreviousSubIssue()"
                    [subIssue]="getSubIssue(element.rowId, column.column)"
                    [subIssueIndex]="
                      getSubIssueIndex(element.rowId, column.column)
                    "
                    [currentSubIssueIndex]="currentSubIssueIndex"
                  >
                  </app-view-issue-link>
                }
              </ng-container>
            }
            @if (
              element.total &&
              columnTotals.hasOwnProperty(column.column) &&
              element.total.hasOwnProperty(column.column)
            ) {
              <app-number-input
                [value]="getSubTotal(element.total, column.column)"
                [decimals]="getColumnDecimals(column.column)"
                [displayOnly]="true"
                [isCurrency]="currency"
              >
              </app-number-input>
            }
            @if (element.rowId && column.isTotal) {
              <app-number-input
                [value]="rowTotals[index]"
                [decimals]="getColumnDecimals(column.column)"
                [displayOnly]="true"
                [isCurrency]="currency"
              >
              </app-number-input>
            }
          </td>
          <td mat-footer-cell *matFooterCellDef>
            @if (column.isLabel) {
              <b>
                {{ table.totalRowLabel ?? 'Total' }}
              </b>
            }
            @if (
              !column.hideColumnTotal &&
              columnTotals.hasOwnProperty(column.column) &&
              (!column?.customTotal || column?.customTotal?.length)
            ) {
              <app-number-input
                [value]="columnTotals[column.column]"
                [displayOnly]="true"
                [isCurrency]="currency"
              >
              </app-number-input>
            }
            @if (column.isTotal) {
              <app-number-input
                [value]="overallTotal"
                [decimals]="getColumnDecimals(column.column)"
                [displayOnly]="true"
                [isCurrency]="currency"
              >
              </app-number-input>
            }
          </td>
        </ng-container>
        <ng-container
          [matColumnDef]="getPreviousYearColumnId(column.column)"
          [sticky]="sticky && column.isLabel"
          [stickyEnd]="sticky && column.isTotal"
        >
          <td mat-cell *matCellDef="let element">
            @if (canToggleRowDetails(element)) {
              <div
                class="previous-year-cell"
                [class.active]="
                  !column.isId &&
                  !column.isLabel &&
                  !column.isTotal &&
                  !readOnly
                "
                [attr.aria-hidden]="
                  !isPreviousYearExpanded(getPreviousYearRowKey(element))
                "
              >
                @if (column.isLabel) {
                  <span>{{ element[column.column] }}</span>
                }
                @if (column.isTotal) {
                  <app-number-input
                    [value]="getPreviousYearRowTotal(element)"
                    [decimals]="getColumnDecimals(column.column)"
                    [displayOnly]="true"
                    [isCurrency]="currency"
                  >
                  </app-number-input>
                }
                @if (
                  !column.isId &&
                  !column.isLabel &&
                  !column.isTotal &&
                  element.hasOwnProperty(column.column)
                ) {
                  <app-number-input
                    [value]="getPreviousYearValue(element, column.column)"
                    [decimals]="getColumnDecimals(column.column)"
                    [displayOnly]="true"
                    [isCurrency]="currency"
                  >
                  </app-number-input>
                }
              </div>
            }
          </td>
        </ng-container>
      }
      <ng-container
        [matColumnDef]="showPreviousYearColumn"
        [stickyEnd]="sticky"
      >
        <th scope="col" mat-header-cell *matHeaderCellDef>
          @if (previousReportingPeriodYear !== null) {
            SHOW {{ previousReportingPeriodYear }}
          }
        </th>
        <td mat-cell *matCellDef="let element">
          @if (canToggleRowDetails(element)) {
            <button
              mat-icon-button
              type="button"
              (click)="togglePreviousYear(getPreviousYearRowKey(element))"
              [attr.aria-expanded]="
                isPreviousYearExpanded(getPreviousYearRowKey(element))
              "
              [attr.aria-label]="
                (isPreviousYearExpanded(getPreviousYearRowKey(element))
                  ? 'Hide '
                  : 'Show ') +
                previousReportingPeriodYear +
                ' for ' +
                (element.rowLabel ?? getPreviousYearRowKey(element))
              "
            >
              <mat-icon
                class="caret"
                [class.flipped]="
                  isPreviousYearExpanded(getPreviousYearRowKey(element))
                "
              >
                expand_more
              </mat-icon>
            </button>
          }
        </td>
        <td mat-footer-cell *matFooterCellDef></td>
      </ng-container>
      <ng-container
        [matColumnDef]="previousYearPlaceholderColumn"
        [stickyEnd]="sticky"
      >
        <td mat-cell *matCellDef="let element"></td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns"
        [class.subheading]="!row.rowId && !row.total && !row.isOtherSpecify"
        [class.subtotal]="!row.rowId && row.total"
        [class.otherSpecify]="row.isOtherSpecify"
      ></tr>
      @if (displayPreviousYearColumn) {
        <tr
          mat-row
          *matRowDef="let row; columns: previousYearRowColumns"
          class="previous-year-row"
          [class.expanded]="
            canToggleRowDetails(row) &&
            isPreviousYearExpanded(getPreviousYearRowKey(row))
          "
        ></tr>
      }
      <tr
        mat-footer-row
        class="sticky-footer"
        *matFooterRowDef="displayedColumns"
        [hidden]="table.hideTotalRow"
      ></tr>
    </table>
  </div>
  <div class="action-buttons">
    @if (!readOnly) {
      <button
        mat-flat-button
        color="primary"
        type="submit"
        class="submit-button"
        [disabled]="tableForm.invalid || disabled || tableForm.pristine"
      >
        @if (disabled) {
          Saving...
        }
        @if (!disabled) {
          Save
        }
      </button>
    }
    <button mat-stroked-button type="button" (click)="goBack()">Go Back</button>
  </div>
</form>
